/**
 * Notification History
 * Displays recent on-call ticket checks and notification history
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Text,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  type IColumn,
  MessageBarType,
  DefaultButton,
  Link,
  Icon,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  Spinner,
  SpinnerSize
} from '@fluentui/react';
import { useOnCallManager } from '../contexts/OnCallManagerContext';
import type { OnCallTicketCheck, OnCallSmsMessageSummary } from '../types';
import { formatUtcAsLocalDateTime } from '../utils/dateTime';
import { useConnectWiseLinks } from '@/connectwise';

interface NotificationHistoryProps {
  onNotification: (type: MessageBarType, message: string) => void;
}

const NotificationHistory: React.FC<NotificationHistoryProps> = ({ onNotification }) => {
  const { recentNotifications, refreshRecentNotifications, loading } = useOnCallManager();
  const { ticketUrl } = useConnectWiseLinks();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsDialogTicket, setSmsDialogTicket] = useState<string>('');
  const [smsDialogMessages, setSmsDialogMessages] = useState<OnCallSmsMessageSummary[]>([]);

  // Load notifications on mount only. Do not depend on refresh/onNotification —
  // those can change identity and re-fire this effect into a fetch loop.
  useEffect(() => {
    refreshRecentNotifications().catch(err => {
      const message = err instanceof Error ? err.message : 'Failed to load notifications';
      onNotification(MessageBarType.error, `Error: ${message}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshRecentNotifications();
      onNotification(MessageBarType.success, 'Notification history refreshed');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh notifications';
      onNotification(MessageBarType.error, `Error: ${message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  const openSmsDialog = useCallback((ticketLabel: string, messages: OnCallSmsMessageSummary[]) => {
    setSmsDialogTicket(ticketLabel);
    setSmsDialogMessages(messages);
    setSmsDialogOpen(true);
  }, []);

  // Memoize columns so DetailsList (justified layout) does not enter an update loop
  // from a new columns array identity on every render.
  const columns: IColumn[] = useMemo(
    () => [
      {
        key: 'ticketNumber',
        name: 'Ticket',
        fieldName: 'ticketNumber',
        minWidth: 80,
        maxWidth: 100,
        onRender: (item: OnCallTicketCheck) => (
          <Link
            href={ticketUrl(item.ticketId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.ticketNumber || item.ticketId}
          </Link>
        )
      },
      {
        key: 'summary',
        name: 'Summary',
        fieldName: 'summary',
        minWidth: 200,
        maxWidth: 400,
        isMultiline: true,
        onRender: (item: OnCallTicketCheck) => (
          <Text variant="small" styles={{ root: { wordBreak: 'break-word' } }}>
            {item.summary || 'N/A'}
          </Text>
        )
      },
      {
        key: 'scheduledCheckTime',
        name: 'Scheduled',
        fieldName: 'scheduledCheckTime',
        minWidth: 140,
        maxWidth: 180,
        onRender: (item: OnCallTicketCheck) => formatUtcAsLocalDateTime(item.scheduledCheckTime)
      },
      {
        key: 'checkPerformed',
        name: 'Check Status',
        minWidth: 100,
        maxWidth: 150,
        onRender: (item: OnCallTicketCheck) => (
          <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
            <Icon
              iconName={item.checkPerformed ? 'CheckMark' : 'StatusCircleQuestionMark'}
              styles={{
                root: {
                  color: item.checkPerformed ? '#107c10' : '#a19f9d',
                  fontSize: 16
                }
              }}
            />
            <Text variant="small">
              {item.checkPerformed ? formatUtcAsLocalDateTime(item.checkPerformedAt) : 'Pending'}
            </Text>
          </Stack>
        )
      },
      {
        key: 'notificationSent',
        name: 'Notification',
        minWidth: 100,
        maxWidth: 150,
        onRender: (item: OnCallTicketCheck) => (
          <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
            <Icon
              iconName={item.notificationSent ? 'MailCheck' : item.checkPerformed ? 'Mail' : 'StatusCircleQuestionMark'}
              styles={{
                root: {
                  color: item.notificationSent ? '#107c10' : item.checkPerformed ? '#605e5c' : '#a19f9d',
                  fontSize: 16
                }
              }}
            />
            <Text variant="small">
              {item.notificationSent ? formatUtcAsLocalDateTime(item.notificationSentAt) : 'Not sent'}
            </Text>
          </Stack>
        )
      },
      {
        key: 'sms',
        name: 'SMS',
        minWidth: 90,
        maxWidth: 150,
        onRender: (item: OnCallTicketCheck) => {
          const smsCount = item.smsCount ?? 0;
          const smsMessages = item.smsMessages ?? [];

          if (smsCount <= 0) {
            return <Text variant="small">None</Text>;
          }

          if (smsCount === 1) {
            const message = smsMessages[0];
            const status = message?.latestStatus ?? (message?.sendSucceeded ? 'sent' : 'failed');
            return <Text variant="small">{status}</Text>;
          }

          return (
            <Link
              onClick={() => {
                openSmsDialog(item.ticketNumber || item.ticketId.toString(), smsMessages);
              }}
            >
              Multiple
            </Link>
          );
        }
      },
      {
        key: 'result',
        name: 'Result',
        minWidth: 150,
        maxWidth: 250,
        onRender: (item: OnCallTicketCheck) => {
          const isCancellation = item.processingError?.toLowerCase().includes('notification cancelled') ?? false;

          if (isCancellation) {
            return (
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Ticket Acknowledged
              </Text>
            );
          }

          if (item.processingError) {
            return (
              <Text variant="small" styles={{ root: { color: '#d13438' } }}>
                Error: {item.processingError}
              </Text>
            );
          }
          if (item.checkResult) {
            return (
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                {item.checkResult}
              </Text>
            );
          }
          return <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>N/A</Text>;
        }
      }
    ],
    [openSmsDialog, ticketUrl]
  );

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
          Notification History
        </Text>
        <DefaultButton
          text="Refresh"
          iconProps={{ iconName: 'Refresh' }}
          onClick={handleRefresh}
          disabled={isRefreshing || loading}
        />
      </Stack>

      <Text variant="medium" styles={{ root: { color: '#605e5c' } }}>
        Displays the most recent 20 ticket checks and their notification status.
      </Text>

      {loading || isRefreshing ? (
        <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
          <Spinner size={SpinnerSize.large} label="Loading notification history..." />
        </Stack>
      ) : recentNotifications.length === 0 ? (
        <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
          <Text variant="large" styles={{ root: { color: '#a19f9d' } }}>
            No notifications found
          </Text>
        </Stack>
      ) : (
        <DetailsList
          items={recentNotifications}
          columns={columns}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
          isHeaderVisible={true}
          styles={{
            root: {
              '.ms-DetailsRow': {
                borderBottom: '1px solid #edebe9'
              }
            }
          }}
        />
      )}

      <Dialog
        hidden={!smsDialogOpen}
        onDismiss={() => setSmsDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Associated SMS Messages',
          subText: `Ticket ${smsDialogTicket}`
        }}
      >
        <Stack tokens={{ childrenGap: 8 }}>
          {smsDialogMessages.map((message) => {
            const status = message.latestStatus ?? (message.sendSucceeded ? 'sent' : 'failed');
            const statusColor = status.toLowerCase() === 'failed' || status.toLowerCase() === 'undelivered' ? '#d13438' : '#107c10';

            return (
              <Stack key={message.id} tokens={{ childrenGap: 2 }} styles={{ root: { padding: '8px 0', borderBottom: '1px solid #edebe9' } }}>
                <Text variant="small"><strong>Recipient:</strong> {message.recipientUserId}</Text>
                <Text variant="small"><strong>Phone:</strong> {message.recipientPhoneNumber}</Text>
                <Text variant="small" styles={{ root: { color: statusColor } }}><strong>Status:</strong> {status}</Text>
              </Stack>
            );
          })}
        </Stack>
        <DialogFooter>
          <PrimaryButton text="Close" onClick={() => setSmsDialogOpen(false)} />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};

export default NotificationHistory;
